import { forwardRef } from "react"
import { Inspect } from "lucide-react"
import { SearchResult } from "../../../types"
import If from "../if"
import { useAppSelector } from "../../hooks"
import { selectUserDetails } from "../../../redux/slices/auth"
import { getSearchResultDetails } from "./result-details"

interface Props {
  data: SearchResult
  isAdmin: boolean
  isFirstItem: boolean
  isActive: boolean
  onClickItem: (result: SearchResult) => void
  handleMasquerade: (loid: string) => void
  closeModal: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
}

const SearchResultItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      data,
      isAdmin,
      isFirstItem,
      isActive,
      onClickItem,
      handleMasquerade,
      closeModal,
      onKeyDown
    },
    ref
  ) => {
    const user = useAppSelector(selectUserDetails)

    const showMasquerade =
      isAdmin && data.type === "loan_officer" && user?.is_masq !== "1"

    const onMasquerade = () => {
      handleMasquerade(data.id)
      closeModal()
    }

    const details = getSearchResultDetails(data)

    return (
      <>
        {isFirstItem && (
          <div className="sticky top-0 z-10 py-1.5 px-6 font-semibold text-xs text-neutral-500 border-b bg-neutral-100 first-letter:uppercase">
            {data.type.split("_").join(" ")}s
          </div>
        )}
        <div
          ref={ref}
          role="option"
          aria-selected={isActive}
          tabIndex={-1}
          onClick={() => onClickItem(data)}
          onKeyDown={onKeyDown}
          className={`group text-sm flex justify-between py-2 px-6 border-b border-neutral-100 cursor-pointer hover:bg-primary-400 hover:text-white transition-colors focus:outline-none ${
            isActive ? "bg-primary-400 text-white" : "bg-white"
          }`}
        >
          <div className="flex-1 min-w-0">
            <div className="truncate">{data.item}</div>
            <If
              condition={details.length > 0}
              then={
                <div
                  className={`text-xs truncate ${
                    isActive
                      ? "text-white/80"
                      : "text-neutral-500 group-hover:text-white/80"
                  }`}
                >
                  {details.join(" · ")}
                </div>
              }
            />
          </div>
          <If
            condition={showMasquerade}
            then={
              <div
                className={`ml-4 flex shrink-0 items-center self-center text-xs font-medium hover:underline ${
                  isActive
                    ? "text-white/90"
                    : "text-neutral-500 group-hover:text-white/90"
                }`}
                onClick={e => {
                  e.stopPropagation()
                  onMasquerade()
                }}
              >
                <Inspect size={14} />
                <span className="ml-1.5">Masquerade</span>
              </div>
            }
          />
        </div>
      </>
    )
  }
)

SearchResultItem.displayName = "SearchResultItem"

export default SearchResultItem
